import { useQuery } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';

export type AllowedInContext = 'event' | 'flow' | 'break' | 'session';

/** virtualMedia = playlist item in the media slot; infoOverlay = placed on a slide template by slug. */
export type CompositeRole = 'virtualMedia' | 'infoOverlay';

export interface CompositeComponentDto {
  id: string;
  name: string;
  slug: string;
  definition: { version: number; components: unknown[] };
  scope: string;
  role?: CompositeRole;
  allowedIn?: AllowedInContext[];
  applicability?: {
    eventTypes?: string[];
    flowScopes?: string[];
    sessionTypes?: string[];
    slotContentTypes?: string[];
  };
  sortOrderByScope?: Record<string, number>;
  updatedAt: string;
}

export const GET_COMPOSITE_COMPONENTS_QUERY_KEY = 'get-composite-components';

export function isInfoOverlayComposite(composite: Pick<CompositeComponentDto, 'role'>): boolean {
  return composite.role === 'infoOverlay';
}

export const useGetCompositeComponentsQuery = (context?: AllowedInContext) => {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: [GET_COMPOSITE_COMPONENTS_QUERY_KEY, context],
    queryFn: async () => {
      const token = await getToken();
      const url = new URL(`${import.meta.env.VITE_API_URL}/composite-components`);
      if (context) url.searchParams.set('context', context);
      return sendJSONRequest({ url: url.toString(), method: METHOD.GET, token: token ?? undefined }) as Promise<
        CompositeComponentDto[]
      >;
    },
  });
};
